import { inject, Injectable, signal } from '@angular/core';

import { Decimal } from 'decimal.js';

import { ShellStore } from '@iw/shell';
import { DictionariesStore } from '@iw/store';

import { Currency } from '../entities';

type ConversionDirection = 'fromTo' | 'toFrom';

interface ConversionParams {
  amount: string;
  rounding: Decimal.Rounding;
  rate: number;
  decimals: number;
}

@Injectable({ providedIn: 'root' })
export class AmountConversionService {
  readonly #roundFromTo = Decimal.ROUND_DOWN;
  readonly #roundToFrom = Decimal.ROUND_UP;
  readonly #dictStore = inject(DictionariesStore);
  readonly #shellStore = inject(ShellStore);

  // Uncomment when api will be ready
  // readonly convertedCurrency = this.#shellStore.me()?.convertedCurrency;
  readonly #convertedCurrency = 'USD';
  readonly currentRate = signal(0);

  convert(
    amount: string,
    fromCur: Currency,
    toCur: Currency,
    direction: ConversionDirection = 'fromTo',
  ): string {
    this.currentRate.set(0);

    if (fromCur.symbol === toCur.symbol) {
      return amount || '0';
    }

    const rate = this.#getRate(fromCur.symbol, toCur.symbol);

    const params: ConversionParams =
      direction === 'fromTo'
        ? {
            amount,
            rate,
            rounding: this.#roundFromTo,
            decimals: toCur.decimals,
          }
        : {
            amount,
            rate: 1 / rate,
            rounding: this.#roundToFrom,
            decimals: fromCur.decimals,
          };

    return this.#applyConversion(params);
  }

  #getRate(fromCur: string, toCur: string): number {
    let rate: number;

    const directRate = this.#dictStore.getRate(fromCur, toCur);

    if (Number(directRate)) {
      rate = Number(directRate);
    } else {
      const r1 = this.#dictStore.getRate(fromCur, this.#convertedCurrency);
      const r2 = this.#dictStore.getRate(this.#convertedCurrency, toCur);

      rate = new Decimal(r1).times(r2).toNumber();
    }

    this.currentRate.set(rate);

    return rate;
  }

  #applyConversion(params: ConversionParams): string {
    const { amount, rate, decimals, rounding } = params;

    if (!amount || !rate) return '0';

    return new Decimal(amount)
      .times(rate)
      .toDecimalPlaces(decimals, rounding)
      .toFixed(decimals);
  }
}
